<template>
  <div class="share">
    <van-tabs v-model:active="activeTab">
      <van-tab title="可分享事项">
        <van-list
          v-model:loading="loading"
          :finished="finished"
          finished-text="没有更多了"
        >
          <van-cell-group v-for="item in shareableItems" :key="item.id">
            <van-cell :title="item.get('title')">
              <template #right-icon>
                <van-button size="small" @click="showShareOptions(item)">
                  分享
                </van-button>
              </template>
            </van-cell>
          </van-cell-group>
        </van-list>
      </van-tab>
      
      <van-tab title="协作事项">
        <van-list
          v-model:loading="collaborationLoading"
          :finished="collaborationFinished"
          finished-text="没有更多了"
        >
          <van-cell-group v-for="item in collaborationItems" :key="item.id">
            <van-cell 
              :title="item.get('title')"
              :label="`创建者: ${item.get('owner').get('username')}`"
            >
              <template #right-icon>
                <van-button size="small" @click="showCollaborators(item)">
                  协作者
                </van-button>
              </template>
            </van-cell>
          </van-cell-group>
        </van-list>
      </van-tab>
    </van-tabs>

    <van-action-sheet
      v-model:show="showShare"
      :actions="shareActions"
      @select="onShareSelect"
      cancel-text="取消"
    />

    <van-popup
      v-model:show="showCollaboratorsList"
      position="bottom"
      round
    >
      <div class="collaborators-popup">
        <div class="popup-title">协作者列表</div>
        <van-cell-group>
          <van-cell
            v-for="collaborator in currentCollaborators"
            :key="collaborator.id"
            :title="collaborator.get('username')"
          >
            <template #right-icon>
              <van-button 
                size="small" 
                type="danger"
                @click="removeCollaborator(collaborator)"
              >
                移除
              </van-button>
            </template>
          </van-cell>
        </van-cell-group>
        <van-button 
          type="primary" 
          block 
          @click="showAddCollaborator"
          class="add-collaborator-btn"
        >
          添加协作者
        </van-button>
      </div>
    </van-popup>

    <van-dialog
      v-model:show="showAddCollaboratorDialog"
      title="添加协作者"
      show-cancel-button
      @confirm="addCollaborator"
    >
      <van-field
        v-model="newCollaboratorEmail"
        label="邮箱"
        placeholder="请输入协作者邮箱"
      />
    </van-dialog>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import AV from 'leancloud-storage'
import { showToast } from 'vant'
import { useRoute } from 'vue-router'

export default {
  name: 'Share',
  setup() {
    const activeTab = ref(0)
    const loading = ref(false)
    const finished = ref(true)
    const collaborationLoading = ref(false)
    const collaborationFinished = ref(true)
    const showShare = ref(false)
    const showCollaboratorsList = ref(false)
    const showAddCollaboratorDialog = ref(false)
    const shareableItems = ref([])
    const collaborationItems = ref([])
    const currentItem = ref(null)
    const currentCollaborators = ref([])
    const newCollaboratorEmail = ref('')
    const route = useRoute()

    const shareActions = [
      { name: '复制链接', value: 'copy' },
      { name: '微信分享', value: 'wechat' },
      { name: '邮件分享', value: 'email' }
    ]

    const fetchShareableItems = async () => {
      const query = new AV.Query('ChecklistItem')
      query.equalTo('user', AV.User.current())
      query.equalTo('shareable', true)
      try {
        shareableItems.value = await query.find()
      } catch (error) {
        showToast('获取数据失败')
      }
    }

    const fetchCollaborationItems = async () => {
      const query = new AV.Query('ChecklistItem')
      const currentUser = AV.User.current()
      query.containedIn('collaborators', [currentUser.id])
      try {
        collaborationItems.value = await query.find()
      } catch (error) {
        showToast('获取数据失败')
      }
    }

    const showShareOptions = (item) => {
      currentItem.value = item
      showShare.value = true
    }

    const onShareSelect = async (action) => {
      if (!currentItem.value) return
      
      switch (action.value) {
        case 'copy':
          const shareLink = `${window.location.origin}/share/${currentItem.value.id}`
          await navigator.clipboard.writeText(shareLink)
          showToast('链接已复制')
          break
        case 'wechat':
          // 实现微信分享逻辑
          showToast('微信分享功能开发中')
          break
        case 'email':
          // 实现邮件分享逻辑
          window.location.href = `mailto:?subject=分享核对事项&body=查看核对事项：${window.location.origin}/share/${currentItem.value.id}`
          break
      }
      showShare.value = false
    }

    const showCollaborators = async (item) => {
      currentItem.value = item
      const collaboratorIds = item.get('collaborators') || []
      
      try {
        if (collaboratorIds.length > 0) {
          const query = new AV.Query('_User')
          query.containedIn('objectId', collaboratorIds)
          currentCollaborators.value = await query.find()
        } else {
          currentCollaborators.value = []
        }
        showCollaboratorsList.value = true
      } catch (error) {
        showToast('获取协作者失败')
      }
    }

    const showAddCollaborator = () => {
      showAddCollaboratorDialog.value = true
    }

    const addCollaborator = async () => {
      if (!newCollaboratorEmail.value || !currentItem.value) return
      
      try {
        const query = new AV.Query('_User')
        query.equalTo('email', newCollaboratorEmail.value)
        const user = await query.first()
        
        if (!user) {
          showToast('用户不存在')
          return
        }

        // 获取当前的协作者数组
        let collaborators = currentItem.value.get('collaborators') || []
        
        // 检查用户是否已经是协作者
        if (collaborators.includes(user.id)) {
          showToast('该用户已是协作者')
          return
        }

        // 添加新协作者的 ID
        collaborators.push(user.id)
        currentItem.value.set('collaborators', collaborators)
        await currentItem.value.save()
        
        currentCollaborators.value.push(user)
        showToast('添加成功')
        newCollaboratorEmail.value = ''
      } catch (error) {
        showToast('添加失败')
      }
    }

    const removeCollaborator = async (collaborator) => {
      if (!currentItem.value) return
      
      try {
        let collaborators = currentItem.value.get('collaborators') || []
        collaborators = collaborators.filter(id => id !== collaborator.id)
        
        currentItem.value.set('collaborators', collaborators)
        await currentItem.value.save()
        
        currentCollaborators.value = currentCollaborators.value.filter(
          c => c.id !== collaborator.id
        )
        showToast('移除成功')
      } catch (error) {
        showToast('移除失败')
      }
    }

    onMounted(() => {
      fetchShareableItems()
      fetchCollaborationItems()
      
      // 处理从详情页跳转过来的情况
      const itemId = route.query.itemId
      if (itemId) {
        // 查找对应的事项
        const item = shareableItems.value.find(item => item.id === itemId)
        if (item) {
          // 自动打开协作者添加弹窗
          currentItem.value = item
          showAddCollaboratorDialog.value = true
        }
      }
    })

    return {
      activeTab,
      loading,
      finished,
      collaborationLoading,
      collaborationFinished,
      showShare,
      showCollaboratorsList,
      showAddCollaboratorDialog,
      shareableItems,
      collaborationItems,
      currentCollaborators,
      newCollaboratorEmail,
      shareActions,
      showShareOptions,
      onShareSelect,
      showCollaborators,
      showAddCollaborator,
      addCollaborator,
      removeCollaborator
    }
  }
}
</script>

<style scoped>
.share {
  height: 100%;
}
.collaborators-popup {
  padding: 16px;
}
.popup-title {
  text-align: center;
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 16px;
}
.add-collaborator-btn {
  margin-top: 16px;
}
</style> 